/**
 * React Hook for Device Tracking
 * Manages device fingerprinting and session initialization for guest users
 * ONLY affects non-logged-in users
 */

import { useState, useEffect } from 'react'
import { initDeviceTracking } from '../utils/deviceFingerprint'
import { initializeDeviceSession, updateDeviceActivity } from '../services/deviceTrackingService'

/**
 * Custom hook for device tracking
 * @param {string} restaurantId - Restaurant UUID
 * @param {string} tableId - Table UUID
 * @param {Object} currentUser - Current logged-in user (if any)
 * @returns {Object} - { deviceSession, loading, error }
 */
export const useDeviceTracking = (restaurantId, tableId, currentUser = null) => {
  const [deviceSession, setDeviceSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Skip device tracking for logged-in users
    if (currentUser) {
      console.log('👤 User is logged in - skipping device tracking')
      setLoading(false)
      return
    }

    // Skip if no restaurant or table ID
    if (!restaurantId || !tableId) {
      console.log('⚠️ Missing restaurantId or tableId - skipping device tracking')
      setLoading(false)
      return
    }

    let mounted = true

    const initializeTracking = async () => {
      try {
        console.log('🚀 [useDeviceTracking] Starting device tracking initialization...')
        
        // Step 1: Initialize device fingerprinting
        const { deviceId, deviceInfo, isNew } = await initDeviceTracking()
        
        if (!mounted) return
        
        console.log('🔐 [useDeviceTracking] Device fingerprint:', deviceId, isNew ? '(NEW)' : '(EXISTING)')
        
        // Step 2: Initialize device session in database
        const sessionData = await initializeDeviceSession(restaurantId, tableId)
        
        if (!mounted) return
        
        console.log('✅ [useDeviceTracking] Device session initialized:', sessionData)
        
        setDeviceSession({
          ...sessionData,
          deviceId,
          deviceInfo,
          isNewDevice: isNew
        })
        
        setLoading(false)
      } catch (err) {
        console.error('❌ [useDeviceTracking] Error initializing device tracking:', err)
        
        if (!mounted) return
        
        setError(err)
        setLoading(false)
      }
    }

    initializeTracking()

    // Cleanup function
    return () => {
      mounted = false
    }
  }, [restaurantId, tableId, currentUser])

  // Update device activity periodically (every 5 minutes)
  useEffect(() => {
    if (!deviceSession?.deviceId || currentUser) {
      return
    }

    const intervalId = setInterval(() => {
      updateDeviceActivity(deviceSession.deviceId)
    }, 5 * 60 * 1000) // 5 minutes

    return () => {
      clearInterval(intervalId)
    }
  }, [deviceSession?.deviceId, currentUser])

  return {
    deviceSession,
    loading,
    error
  }
}

export default useDeviceTracking
